import { useState, useRef } from 'react';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import AddIcon from '@mui/icons-material/Add';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import useApiData from '../../services/useApiData.js';
import api from '../../services/axiosInterceptor.js';
import SnackbarEx from '../Helpers/SnackbarEx.jsx';

const INVOICE_FIELDS = [
    { key: 'invoice_id', label: 'Invoice / Order ID', hint: 'Column with the order ID — or enter it manually at upload.' },
    { key: 'supplier_name', label: 'Supplier name', hint: 'Usually a fixed value for this preset (e.g. Amazon).' },
    { key: 'invoice_date', label: 'Invoice date', hint: 'Column with the date — or enter it manually at upload.' },
];

const LINE_ITEM_FIELDS = [
    { key: 'item_number', label: 'Item # / ASIN', hint: 'Product ID column.' },
    { key: 'description', label: 'Description', hint: 'Product name column.' },
    { key: 'price', label: 'Unit price', hint: 'Cost column.' },
    { key: 'qty', label: 'Quantity', hint: 'Units column.' },
];

/** Product fields available for CSV auto-match (keep in sync with server allowlist). */
const PRODUCT_AUTO_MATCH_FIELDS = [
    { value: 'asin', label: 'ASIN' },
    { value: 'listingField', label: 'Listing Field' },
    { value: 'internalSkuId', label: 'Internal SKU ID' },
    { value: 'itemName', label: 'Item Name' },
    { value: 'upc', label: 'UPC' },
    { value: 'gtin', label: 'GTIN' },
    { value: 'walmartLinkedSku', label: 'Walmart Linked SKU' },
    { value: 'ebayLinkedSku', label: 'eBay Linked SKU' },
];

const EMPTY_PRODUCT_AUTO_MATCH = {
    enabled: false,
    csvColumn: '',
    productField: '',
};

const PDF_INVOICE_FIELDS = [
    { key: 'invoice_id', label: 'Invoice ID', placeholder: 'e.g. Invoice #, Order Number' },
    { key: 'barcode', label: 'Barcode', placeholder: 'e.g. Barcode, MAC-…' },
    { key: 'invoice_date', label: 'Invoice date', placeholder: 'e.g. Date, Invoice Date' },
    { key: 'customer_identifier', label: 'Customer ID', placeholder: 'e.g. Account #, Customer ID' },
    { key: 'subTotal', label: 'Subtotal', placeholder: 'e.g. Subtotal, Sub Total, Sales Total' },
    { key: 'shipping', label: 'Shipping', placeholder: 'e.g. Shipping, Freight, Delivery' },
    { key: 'sales_tax', label: 'Sales tax', placeholder: 'e.g. Tax, Sales Tax' },
    { key: 'total', label: 'Total', placeholder: 'e.g. Total, Grand Total' },
];

const PDF_GROUPS = [
    {
        group: 'supplier', label: 'Supplier block',
        fields: [
            { key: 'name', label: 'Name', placeholder: 'e.g. Sold by, Vendor' },
            { key: 'address', label: 'Address', placeholder: 'e.g. Address, Location' },
            { key: 'phone', label: 'Phone', placeholder: 'e.g. Phone' },
        ],
    },
    {
        group: 'customer', label: 'Customer block',
        fields: [
            { key: 'name', label: 'Name', placeholder: 'e.g. Bill To, Sold To' },
            { key: 'address', label: 'Address', placeholder: 'e.g. Ship To' },
            { key: 'phone', label: 'Phone', placeholder: 'e.g. Phone' },
        ],
    },
    {
        group: 'items', label: 'Line item columns',
        fields: [
            { key: 'item_number', label: 'Item #', placeholder: 'e.g. VALPN, SKU, Lot #' },
            { key: 'description', label: 'Description', placeholder: 'e.g. Description, Item Name' },
            { key: 'qty', label: 'Qty', placeholder: 'e.g. Qty, Quantity' },
            { key: 'unit_price', label: 'Unit price', placeholder: 'e.g. Price, Unit Price' },
            { key: 'total', label: 'Line total', placeholder: 'e.g. Amount, Ext. Price' },
            { key: 'unit', label: 'Unit', placeholder: 'e.g. UOM, Unit' },
        ],
    },
];

/** Internal field → label as printed on that supplier's PDF. Empty = skip. */
const DEFAULT_PDF_FIELD_MAP = {
    invoice_id: 'Invoice ID',
    barcode: 'Barcode',
    invoice_date: 'Invoice Date',
    customer_identifier: 'Customer ID',
    customer: { name: 'Customer Name', address: 'Customer Address', phone: 'Customer Phone' },
    supplier: { name: 'Supplier Name', address: 'Supplier Address', phone: 'Supplier Phone' },
    items: {
        item_number: 'Item Number',
        unit: 'Unit',
        qty: 'Quantity',
        description: 'Description',
        unit_price: 'Unit Price',
        total: 'Line Total',
    },
    subTotal: 'Subtotal',
    shipping: 'Shipping',
    sales_tax: 'Sales Tax',
    total: 'Total',
};

/** Convert legacy boolean schema or mixed shapes into string label map. */
function toPdfFieldMap(raw) {
    const defaults = DEFAULT_PDF_FIELD_MAP;
    if (!raw || typeof raw !== 'object') {
        return JSON.parse(JSON.stringify(defaults));
    }

    const leaf = (val, fallback) => {
        if (typeof val === 'string') return val;
        if (typeof val === 'boolean') return val ? fallback : '';
        if (val && typeof val === 'object' && 'label' in val) {
            return val.enabled === false ? '' : (val.label || fallback || '');
        }
        return fallback ?? '';
    };

    return {
        invoice_id: leaf(raw.invoice_id, defaults.invoice_id),
        barcode: leaf(raw.barcode, defaults.barcode),
        invoice_date: leaf(raw.invoice_date, defaults.invoice_date),
        customer_identifier: leaf(raw.customer_identifier, defaults.customer_identifier),
        customer: {
            name: leaf(raw.customer?.name, defaults.customer.name),
            address: leaf(raw.customer?.address, defaults.customer.address),
            phone: leaf(raw.customer?.phone, defaults.customer.phone),
        },
        supplier: {
            name: leaf(raw.supplier?.name, defaults.supplier.name),
            address: leaf(raw.supplier?.address, defaults.supplier.address),
            phone: leaf(raw.supplier?.phone, defaults.supplier.phone),
        },
        items: {
            item_number: leaf(raw.items?.item_number, defaults.items.item_number),
            unit: leaf(raw.items?.unit, defaults.items.unit),
            qty: leaf(raw.items?.qty, defaults.items.qty),
            description: leaf(raw.items?.description, defaults.items.description),
            unit_price: leaf(raw.items?.unit_price, defaults.items.unit_price),
            total: leaf(raw.items?.total, defaults.items.total),
        },
        subTotal: leaf(raw.subTotal, defaults.subTotal),
        shipping: leaf(raw.shipping, defaults.shipping),
        sales_tax: leaf(raw.sales_tax, defaults.sales_tax),
        total: leaf(raw.total, defaults.total),
    };
}

const MANUAL_OPT = '__manual__';
const STATIC_OPT = '__static__';
const EMPTY_MATCH = { nameEquals: [], nameContains: [], addressContains: [], filenameContains: [] };

const EMPTY_FORM = {
    sourceName: '',
    invoicePrefix: '',
    supplierDisplayName: '',
    supplierNameStatic: '',
    invoiceIdSource: 'invoice_id',
    parseDescriptionCount: false,
    matchRules: { ...EMPTY_MATCH },
    invoiceFieldMap: { invoice_id: MANUAL_OPT, supplier_name: STATIC_OPT, invoice_date: MANUAL_OPT },
    lineItemFieldMap: { item_number: '', description: '', price: '', qty: '' },
    hasHeaderRow: true,
    productAutoMatch: { ...EMPTY_PRODUCT_AUTO_MATCH },
    pdfFieldMap: JSON.parse(JSON.stringify(DEFAULT_PDF_FIELD_MAP)),
};

const inputProps = { size: 'small', InputLabelProps: { shrink: true } };

const sectionBox = {
    background: '#f8f9fb',
    border: '1px solid #e8eaed',
    borderRadius: 8,
    padding: '14px 16px',
    marginBottom: 16,
};

const extractCsvHeaders = (file) =>
    new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const firstLine = e.target.result.split('\n')[0];
            const headers = firstLine.split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
            resolve(headers.filter(Boolean));
        };
        reader.readAsText(file);
    });

function Section({ title, description, children }) {
    return (
        <div style={sectionBox}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.25 }}>{title}</Typography>
            {description && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, lineHeight: 1.4 }}>
                    {description}
                </Typography>
            )}
            {children}
        </div>
    );
}

function ChipListEditor({ label, hint, values, onChange, placeholder }) {
    const [draft, setDraft] = useState('');
    const add = () => {
        const v = draft.trim();
        if (!v || values.includes(v)) { setDraft(''); return; }
        onChange([...values, v]);
        setDraft('');
    };
    return (
        <div className="mb-2">
            <Typography variant="body2" fontWeight={500}>{label}</Typography>
            {hint && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {hint}
                </Typography>
            )}
            <div className="d-flex flex-wrap gap-1 mb-1" style={{ minHeight: 24 }}>
                {values.length === 0 && (
                    <Typography variant="caption" color="text.disabled">None yet</Typography>
                )}
                {values.map((v) => (
                    <Chip key={v} label={v} size="small" onDelete={() => onChange(values.filter((x) => x !== v))} />
                ))}
            </div>
            <div className="d-flex gap-1">
                <TextField
                    {...inputProps}
                    placeholder={placeholder}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
                    fullWidth
                />
                <Button size="small" variant="outlined" onClick={add} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                    Add
                </Button>
            </div>
        </div>
    );
}

function MappingRow({ fieldDef, value, onChange, headers, allowManual, allowStatic }) {
    const specialOptions = [
        ...(allowManual ? [{ label: 'Enter manually at upload', value: MANUAL_OPT }] : []),
        ...(allowStatic ? [{ label: 'Always use a fixed value…', value: STATIC_OPT }] : []),
    ];
    const allOptions = [
        ...specialOptions,
        ...headers.map((h) => ({ label: h, value: h })),
    ];
    const selected = allOptions.find((o) => o.value === value) ?? null;

    return (
        <div className="row align-items-start g-2 mb-2">
            <div className="col-5">
                <Typography variant="body2" fontWeight={500}>{fieldDef.label}</Typography>
                <Typography variant="caption" color="text.secondary">{fieldDef.hint}</Typography>
            </div>
            <div className="col-7">
                <Autocomplete
                    options={allOptions}
                    getOptionLabel={(o) => o.label}
                    value={selected}
                    onChange={(_, v) => onChange(v?.value ?? '')}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            {...inputProps}
                            placeholder={headers.length ? 'Pick a column…' : 'Upload a sample CSV first'}
                        />
                    )}
                    size="small"
                    fullWidth
                    isOptionEqualToValue={(o, v) => o.value === v.value}
                    disabled={headers.length === 0 && !allowManual && !allowStatic}
                />
            </div>
        </div>
    );
}

/** PDF: our field → label as printed on this supplier's invoice */
function PdfMappingRow({ fieldDef, value, onChange }) {
    return (
        <div className="row align-items-start g-2 mb-2">
            <div className="col-5">
                <Typography variant="body2" fontWeight={500}>{fieldDef.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                    Leave blank to skip this field
                </Typography>
            </div>
            <div className="col-7">
                <TextField
                    {...inputProps}
                    fullWidth
                    value={value ?? ''}
                    onChange={(e) => onChange(e.target.value)}
                    placeholder={fieldDef.placeholder || 'Label on the PDF…'}
                />
            </div>
        </div>
    );
}

function matchSummary(config) {
    const r = config.matchRules || {};
    const parts = [
        ...(r.addressContains || []).map((v) => `address “${v}”`),
        ...(r.nameEquals || []).map((v) => `name “${v}”`),
        ...(r.nameContains || []).map((v) => `name has “${v}”`),
        ...(r.filenameContains || []).map((v) => `file “${v}”`),
    ];
    return parts.length ? parts.join(' · ') : 'No auto-detect rules';
}

function ConfigCard({ config, onEdit, onDuplicate, onDelete }) {
    const csvReady = Object.values(config.lineItemFieldMap || {}).every(Boolean);
    const display = config.supplierDisplayName || config.supplierNameStatic || config.sourceName;
    return (
        <div className="card border rounded-3 p-3 mb-2">
            <div className="d-flex justify-content-between align-items-start gap-2">
                <div style={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" fontWeight={600}>{config.sourceName}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Saves to Airtable as <strong>{display}</strong>
                        {config.invoicePrefix ? <> · ID prefix <strong>{config.invoicePrefix}</strong></> : null}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                        Detect when: {matchSummary(config)}
                    </Typography>
                    <div className="d-flex gap-1 mt-1.5" style={{ marginTop: 8 }}>
                        <Chip
                            label={csvReady ? 'CSV mapped' : 'CSV not mapped'}
                            size="small"
                            color={csvReady ? 'success' : 'default'}
                            variant="outlined"
                        />
                        <Chip
                            label={config.invoiceIdSource === 'barcode' ? 'ID from barcode' : 'ID from invoice #'}
                            size="small"
                            variant="outlined"
                        />
                        {config.parseDescriptionCount && (
                            <Chip
                                label="Count × qty"
                                size="small"
                                color="info"
                                variant="outlined"
                            />
                        )}
                        {config.productAutoMatch?.enabled && (
                            <Chip
                                label={`Match → ${
                                    PRODUCT_AUTO_MATCH_FIELDS.find(
                                        (f) => f.value === config.productAutoMatch?.productField
                                    )?.label || config.productAutoMatch?.productField || 'Product'
                                }`}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </div>
                </div>
                <div className="d-flex gap-1 flex-shrink-0">
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => onEdit(config)}>
                            <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                        <IconButton size="small" onClick={() => onDuplicate(config)}>
                            <ContentCopyOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => onDelete(config)}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>
        </div>
    );
}

export default function ImportConfigurations() {
    const { data: configs = [], isLoading, refetch } = useApiData('api/broylesllc/configuration/import-configs');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [csvFileName, setCsvFileName] = useState('');
    const [saving, setSaving] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', color: 'success' });
    const fileInputRef = useRef(null);

    const setInvoiceMap = (key, value) =>
        setForm((p) => ({ ...p, invoiceFieldMap: { ...p.invoiceFieldMap, [key]: value } }));

    const setLineItemMap = (key, value) =>
        setForm((p) => ({ ...p, lineItemFieldMap: { ...p.lineItemFieldMap, [key]: value } }));

    const setMatchRule = (key, values) =>
        setForm((p) => ({ ...p, matchRules: { ...p.matchRules, [key]: values } }));

    const setPdfTop = (key, value) =>
        setForm((p) => ({
            ...p,
            pdfFieldMap: { ...p.pdfFieldMap, [key]: value },
        }));

    const setPdfNested = (group, key, value) =>
        setForm((p) => ({
            ...p,
            pdfFieldMap: {
                ...p.pdfFieldMap,
                [group]: { ...(p.pdfFieldMap[group] || {}), [key]: value },
            },
        }));

    const handleCsvUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setCsvFileName(file.name);
        const headers = await extractCsvHeaders(file);
        setCsvHeaders(headers);
        e.target.value = '';
    };

    const openAdd = () => {
        setForm(JSON.parse(JSON.stringify(EMPTY_FORM)));
        setCsvHeaders([]);
        setCsvFileName('');
        setEditingId(null);
        setIsDuplicating(false);
        setDialogOpen(true);
    };

    const openEdit = (config) => {
        setForm({
            sourceName: config.sourceName,
            invoicePrefix: config.invoicePrefix ?? '',
            supplierDisplayName: config.supplierDisplayName ?? '',
            supplierNameStatic: config.supplierNameStatic ?? '',
            invoiceIdSource: config.invoiceIdSource || 'invoice_id',
            parseDescriptionCount: !!config.parseDescriptionCount,
            matchRules: {
                ...EMPTY_MATCH,
                ...(config.matchRules || {}),
                nameEquals: config.matchRules?.nameEquals || [],
                nameContains: config.matchRules?.nameContains || [],
                addressContains: config.matchRules?.addressContains || [],
                filenameContains: config.matchRules?.filenameContains || [],
            },
            invoiceFieldMap: { ...EMPTY_FORM.invoiceFieldMap, ...config.invoiceFieldMap },
            lineItemFieldMap: { ...EMPTY_FORM.lineItemFieldMap, ...config.lineItemFieldMap },
            hasHeaderRow: config.hasHeaderRow !== false,
            productAutoMatch: {
                ...EMPTY_PRODUCT_AUTO_MATCH,
                ...(config.productAutoMatch || {}),
                enabled: !!config.productAutoMatch?.enabled,
                csvColumn: config.productAutoMatch?.csvColumn || '',
                productField: config.productAutoMatch?.productField || '',
            },
            pdfFieldMap: toPdfFieldMap(config.pdfFieldMap || config.pdfExtractionSchema),
        });
        setCsvHeaders([]);
        setCsvFileName('');
        setEditingId(config._id);
        setIsDuplicating(false);
        setDialogOpen(true);
    };

    const openDuplicate = (config) => {
        setForm({
            sourceName: `${config.sourceName} (copy)`,
            invoicePrefix: config.invoicePrefix ?? '',
            supplierDisplayName: config.supplierDisplayName ?? '',
            supplierNameStatic: config.supplierNameStatic ?? '',
            invoiceIdSource: config.invoiceIdSource || 'invoice_id',
            parseDescriptionCount: !!config.parseDescriptionCount,
            matchRules: {
                ...EMPTY_MATCH,
                ...(config.matchRules || {}),
                nameEquals: [...(config.matchRules?.nameEquals || [])],
                nameContains: [...(config.matchRules?.nameContains || [])],
                addressContains: [...(config.matchRules?.addressContains || [])],
                filenameContains: [...(config.matchRules?.filenameContains || [])],
            },
            invoiceFieldMap: { ...EMPTY_FORM.invoiceFieldMap, ...config.invoiceFieldMap },
            lineItemFieldMap: { ...EMPTY_FORM.lineItemFieldMap, ...config.lineItemFieldMap },
            hasHeaderRow: config.hasHeaderRow !== false,
            productAutoMatch: {
                ...EMPTY_PRODUCT_AUTO_MATCH,
                ...(config.productAutoMatch || {}),
                enabled: !!config.productAutoMatch?.enabled,
                csvColumn: config.productAutoMatch?.csvColumn || '',
                productField: config.productAutoMatch?.productField || '',
            },
            pdfFieldMap: toPdfFieldMap(config.pdfFieldMap || config.pdfExtractionSchema),
        });
        setCsvHeaders([]);
        setCsvFileName('');
        setEditingId(null); // create a new preset
        setIsDuplicating(true);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.sourceName.trim()) {
            setSnackbar({ open: true, message: 'Preset name is required.', color: 'error' });
            return;
        }
        if (form.productAutoMatch?.enabled) {
            if (!String(form.productAutoMatch.csvColumn || '').trim()) {
                setSnackbar({
                    open: true,
                    message: 'Auto-match requires a spreadsheet column.',
                    color: 'error',
                });
                return;
            }
            if (!String(form.productAutoMatch.productField || '').trim()) {
                setSnackbar({
                    open: true,
                    message: 'Auto-match requires a Product field.',
                    color: 'error',
                });
                return;
            }
        }
        const payload = {
            ...form,
            productAutoMatch: {
                enabled: !!form.productAutoMatch?.enabled,
                csvColumn: String(form.productAutoMatch?.csvColumn || '').trim(),
                productField: String(form.productAutoMatch?.productField || '').trim(),
            },
            supplierDisplayName: form.supplierDisplayName || form.supplierNameStatic || form.sourceName,
            supplierNameStatic: form.supplierNameStatic || form.supplierDisplayName || form.sourceName,
        };
        setSaving(true);
        try {
            if (editingId) {
                await api.put(`/api/broylesllc/configuration/import-configs/${editingId}`, payload);
            } else {
                await api.post('/api/broylesllc/configuration/import-configs', payload);
            }
            setSnackbar({ open: true, message: editingId ? 'Preset updated.' : 'Preset created.', color: 'success' });
            setDialogOpen(false);
            refetch();
        } catch (err) {
            const msg = err?.response?.data?.message ?? 'Failed to save preset.';
            setSnackbar({ open: true, message: msg, color: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/api/broylesllc/configuration/import-configs/${deleteTarget._id}`);
            setSnackbar({ open: true, message: `"${deleteTarget.sourceName}" deleted.`, color: 'success' });
            setDeleteTarget(null);
            refetch();
        } catch {
            setSnackbar({ open: true, message: 'Failed to delete preset.', color: 'error' });
        }
    };

    return (
        <div style={{ padding: '20px 0' }}>
            <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                    <Typography variant="subtitle1" fontWeight={600}>Supplier presets</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
                        One preset per vendor (e.g. Amazon, Vista – Monroe). Controls how we detect the supplier on upload, map CSV columns, and which fields PDF OCR looks for.
                    </Typography>
                </div>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={<AddIcon />}
                    onClick={openAdd}
                    sx={{ textTransform: 'none', fontSize: 13, backgroundColor: '#102A43', '&:hover': { backgroundColor: '#1a3d5c' }, flexShrink: 0 }}
                >
                    Add preset
                </Button>
            </div>

            {isLoading ? (
                <div className="d-flex justify-content-center pt-4">
                    <CircularProgress size={24} />
                </div>
            ) : configs.length === 0 ? (
                <div className="text-center py-5">
                    <Typography variant="body2" color="text.secondary">
                        No presets yet. Add one for each supplier you upload invoices from.
                    </Typography>
                </div>
            ) : (
                configs.map((c) => (
                    <ConfigCard
                        key={c._id}
                        config={c}
                        onEdit={openEdit}
                        onDuplicate={openDuplicate}
                        onDelete={setDeleteTarget}
                    />
                ))
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md" scroll="paper">
                <DialogTitle sx={{ pb: 0.5, fontWeight: 600, fontSize: 17 }}>
                    {editingId ? 'Edit supplier preset' : isDuplicating ? 'Duplicate supplier preset' : 'New supplier preset'}
                </DialogTitle>
                <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>
                    {editingId
                        ? 'Changes update this preset in place — renaming will not create a new one.'
                        : isDuplicating
                            ? 'Creates a new preset with the same mappings. Rename it, then save.'
                            : 'Fill in the basics, then only the sections that apply (CSV and/or PDF).'}
                </Typography>

                <DialogContent dividers sx={{ pt: 2 }}>
                    <Section
                        title="1. Basics"
                        description="How this supplier shows up in the app and in Airtable."
                    >
                        <div className="row g-2">
                            <div className="col-md-6">
                                <TextField
                                    {...inputProps}
                                    label="Preset name"
                                    placeholder="e.g. Vista - Monroe"
                                    helperText="Internal label in this list"
                                    value={form.sourceName}
                                    onChange={(e) => setForm((p) => ({ ...p, sourceName: e.target.value }))}
                                    fullWidth
                                />
                            </div>
                            <div className="col-md-6">
                                <TextField
                                    {...inputProps}
                                    label="Airtable supplier name"
                                    placeholder="Name written to Airtable"
                                    helperText="Used for Supplier Name and Platform"
                                    value={form.supplierDisplayName}
                                    onChange={(e) => setForm((p) => ({
                                        ...p,
                                        supplierDisplayName: e.target.value,
                                        supplierNameStatic: e.target.value,
                                    }))}
                                    fullWidth
                                />
                            </div>
                            <div className="col-md-6">
                                <TextField
                                    {...inputProps}
                                    label="Invoice # prefix (optional)"
                                    placeholder="e.g. AM-"
                                    helperText="Prepended to invoice numbers on CSV submit"
                                    value={form.invoicePrefix}
                                    onChange={(e) => setForm((p) => ({ ...p, invoicePrefix: e.target.value }))}
                                    fullWidth
                                />
                            </div>
                            <div className="col-md-6">
                                <TextField
                                    {...inputProps}
                                    select
                                    label="Where is the invoice ID on PDFs?"
                                    helperText="MAC.BID uses the barcode; most others use invoice ID"
                                    value={form.invoiceIdSource}
                                    onChange={(e) => setForm((p) => ({ ...p, invoiceIdSource: e.target.value }))}
                                    fullWidth
                                >
                                    <MenuItem value="invoice_id">Invoice ID field</MenuItem>
                                    <MenuItem value="barcode">Barcode field</MenuItem>
                                </TextField>
                            </div>
                            <div className="col-12">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={!!form.parseDescriptionCount}
                                            onChange={(e) => setForm((p) => ({
                                                ...p,
                                                parseDescriptionCount: e.target.checked,
                                            }))}
                                            size="small"
                                        />
                                    }
                                    label={
                                        <div>
                                            <Typography variant="body2" fontWeight={500}>
                                                Multiply qty by count in description
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                e.g. “44 Ct” × Quantity 4 → 176 inventory units. Leave off if qty is already sellable units.
                                            </Typography>
                                        </div>
                                    }
                                    sx={{ alignItems: 'flex-start', ml: 0, mt: 1 }}
                                />
                            </div>
                        </div>
                    </Section>

                    <Section
                        title="2. Auto-detect on PDF upload"
                        description="Before OCR, we scan the PDF text and filename for these clues and suggest this preset. Leave blank if you’ll always pick it manually."
                    >
                        <div className="row g-2">
                            <div className="col-md-6">
                                <ChipListEditor
                                    label="Address contains"
                                    hint="e.g. Monroe or Sardis — matches text on the PDF"
                                    values={form.matchRules.addressContains}
                                    onChange={(v) => setMatchRule('addressContains', v)}
                                    placeholder="Type a word, press Add"
                                />
                            </div>
                            <div className="col-md-6">
                                <ChipListEditor
                                    label="Supplier name equals"
                                    hint="Exact name as it appears on the PDF"
                                    values={form.matchRules.nameEquals}
                                    onChange={(v) => setMatchRule('nameEquals', v)}
                                    placeholder="e.g. MAC.BID"
                                />
                            </div>
                            <div className="col-md-6">
                                <ChipListEditor
                                    label="Supplier name contains"
                                    hint="Partial match anywhere in the PDF text"
                                    values={form.matchRules.nameContains}
                                    onChange={(v) => setMatchRule('nameContains', v)}
                                    placeholder="e.g. Vista"
                                />
                            </div>
                            <div className="col-md-6">
                                <ChipListEditor
                                    label="Filename contains"
                                    hint="Match against the uploaded file name"
                                    values={form.matchRules.filenameContains}
                                    onChange={(v) => setMatchRule('filenameContains', v)}
                                    placeholder="e.g. amazon"
                                />
                            </div>
                        </div>
                    </Section>

                    <Section
                        title="3. CSV column mapping"
                        description="Only needed if you upload CSVs for this supplier. Upload a sample file so you can pick columns from a list."
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.hasHeaderRow}
                                    onChange={(e) => setForm((p) => ({ ...p, hasHeaderRow: e.target.checked }))}
                                    size="small"
                                />
                            }
                            label={<Typography variant="body2">First row of the CSV is a header</Typography>}
                            sx={{ mb: 1, ml: 0 }}
                        />
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileUploadOutlinedIcon />}
                                onClick={() => fileInputRef.current?.click()}
                                sx={{ textTransform: 'none', fontSize: 12, borderColor: '#ccc', color: '#444' }}
                            >
                                Upload sample CSV
                            </Button>
                            <input ref={fileInputRef} type="file" accept=".csv" hidden onChange={handleCsvUpload} />
                            <Typography variant="caption" color="text.secondary">
                                {csvFileName
                                    ? `${csvFileName} · ${csvHeaders.length} columns found`
                                    : 'Optional — unlocks column pickers below'}
                            </Typography>
                        </div>

                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Invoice fields</Typography>
                        {INVOICE_FIELDS.map((f) => (
                            <div key={f.key}>
                                <MappingRow
                                    fieldDef={f}
                                    value={form.invoiceFieldMap[f.key]}
                                    onChange={(v) => setInvoiceMap(f.key, v)}
                                    headers={csvHeaders}
                                    allowManual={f.key !== 'supplier_name'}
                                    allowStatic={f.key === 'supplier_name'}
                                />
                                {f.key === 'supplier_name' && form.invoiceFieldMap.supplier_name === STATIC_OPT && (
                                    <div className="row mb-2">
                                        <div className="col-7 offset-5">
                                            <TextField
                                                {...inputProps}
                                                label="Fixed supplier value"
                                                placeholder="Usually same as Airtable name"
                                                value={form.supplierNameStatic || form.supplierDisplayName}
                                                onChange={(e) => setForm((p) => ({ ...p, supplierNameStatic: e.target.value }))}
                                                fullWidth
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}

                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1, mt: 1 }}>Line item columns</Typography>
                        {LINE_ITEM_FIELDS.map((f) => (
                            <MappingRow
                                key={f.key}
                                fieldDef={f}
                                value={form.lineItemFieldMap[f.key]}
                                onChange={(v) => setLineItemMap(f.key, v)}
                                headers={csvHeaders}
                                allowManual={false}
                                allowStatic={false}
                            />
                        ))}

                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1, mt: 2 }}>
                            Product auto-match
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={!!form.productAutoMatch?.enabled}
                                    onChange={(e) => setForm((p) => ({
                                        ...p,
                                        productAutoMatch: {
                                            ...EMPTY_PRODUCT_AUTO_MATCH,
                                            ...(p.productAutoMatch || {}),
                                            enabled: e.target.checked,
                                        },
                                    }))}
                                    size="small"
                                />
                            }
                            label={
                                <div>
                                    <Typography variant="body2" fontWeight={500}>
                                        Auto-match Catalog Items to Products
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        On CSV confirm, link each line to a Product by matching a spreadsheet
                                        column to a Product field. Unmatched lines open a Create Product dialog.
                                    </Typography>
                                </div>
                            }
                            sx={{ alignItems: 'flex-start', ml: 0, mb: 1.5 }}
                        />
                        {!!form.productAutoMatch?.enabled && (
                            <div className="row g-2">
                                <div className="col-md-6">
                                    <Autocomplete
                                        options={csvHeaders}
                                        value={form.productAutoMatch?.csvColumn || null}
                                        onChange={(_, v) => setForm((p) => ({
                                            ...p,
                                            productAutoMatch: {
                                                ...EMPTY_PRODUCT_AUTO_MATCH,
                                                ...(p.productAutoMatch || {}),
                                                csvColumn: v || '',
                                            },
                                        }))}
                                        freeSolo
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                {...inputProps}
                                                label="Match value from spreadsheet"
                                                placeholder={
                                                    csvHeaders.length
                                                        ? 'Pick a column…'
                                                        : 'Upload a sample CSV or type a header'
                                                }
                                                helperText="Column header that holds the match value (e.g. ASIN)"
                                            />
                                        )}
                                        size="small"
                                        fullWidth
                                    />
                                </div>
                                <div className="col-md-6">
                                    <Autocomplete
                                        options={PRODUCT_AUTO_MATCH_FIELDS}
                                        getOptionLabel={(o) => o.label}
                                        value={
                                            PRODUCT_AUTO_MATCH_FIELDS.find(
                                                (f) => f.value === form.productAutoMatch?.productField
                                            ) || null
                                        }
                                        onChange={(_, v) => setForm((p) => ({
                                            ...p,
                                            productAutoMatch: {
                                                ...EMPTY_PRODUCT_AUTO_MATCH,
                                                ...(p.productAutoMatch || {}),
                                                productField: v?.value || '',
                                            },
                                        }))}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                {...inputProps}
                                                label="Match against Product field"
                                                placeholder="Pick a Product field…"
                                                helperText="Exact match on this Product field"
                                            />
                                        )}
                                        size="small"
                                        fullWidth
                                        isOptionEqualToValue={(o, v) => o.value === v.value}
                                    />
                                </div>
                            </div>
                        )}
                    </Section>

                    <Section
                        title="4. PDF field mapping"
                        description="Only needed for PDF uploads. Map each of our fields to the label/header as it appears on this supplier's invoice (e.g. Item # → VALPN). Leave blank to skip."
                    >
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Invoice-level</Typography>
                        {PDF_INVOICE_FIELDS.map((f) => (
                            <PdfMappingRow
                                key={f.key}
                                fieldDef={f}
                                value={form.pdfFieldMap[f.key]}
                                onChange={(v) => setPdfTop(f.key, v)}
                            />
                        ))}
                        {PDF_GROUPS.map((group) => (
                            <div key={group.group} className="mt-2">
                                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{group.label}</Typography>
                                {group.fields.map((f) => (
                                    <PdfMappingRow
                                        key={`${group.group}.${f.key}`}
                                        fieldDef={f}
                                        value={form.pdfFieldMap[group.group]?.[f.key] ?? ''}
                                        onChange={(v) => setPdfNested(group.group, f.key, v)}
                                    />
                                ))}
                            </div>
                        ))}
                    </Section>
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', color: '#667085' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        disabled={saving}
                        onClick={handleSave}
                        sx={{ textTransform: 'none', backgroundColor: '#102A43', '&:hover': { backgroundColor: '#1a3d5c' } }}
                    >
                        {saving ? <CircularProgress size={16} sx={{ color: 'white' }} /> : editingId ? 'Save preset' : 'Create preset'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 600, fontSize: 15 }}>Delete preset?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Remove <strong>{deleteTarget?.sourceName}</strong>? Uploads won’t be able to use it anymore.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none', color: '#667085' }}>Cancel</Button>
                    <Button variant="contained" disableElevation color="error" onClick={handleDelete} sx={{ textTransform: 'none' }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <SnackbarEx
                open={snackbar.open}
                message={snackbar.message}
                color={snackbar.color}
                close={() => setSnackbar((p) => ({ ...p, open: false }))}
            />
        </div>
    );
}
